import { QuizConfig } from "@/components/GuidedPath/type";

/**
 * Builds Algolia facet filters from quiz answers
 * Handles custom facetMappings for options that affect multiple facets
 *
 * @param answers - Quiz answers collected so far
 * @param searchableKeys - Set of keys that have searchFilterKey in quiz config
 * @param quizConfig - Full quiz configuration
 * @param excludeKey - Optional key to exclude from filters (used when checking available options)
 * @returns Array of facet filters in Algolia format
 */
export const buildFacetFiltersFromQuiz = (
  answers: Record<string, string | string[]>,
  searchableKeys: Set<string>,
  quizConfig: QuizConfig,
  excludeKey?: string,
): string[][] => {
  const facetFilters: string[][] = [["isActive:true"]];
  const appliedFacets = new Map<string, string[]>();

  Object.entries(answers).forEach(([key, value]) => {
    // Skip non-searchable keys, excluded keys, or empty values
    if (!searchableKeys.has(key) || key === excludeKey || !value) {
      return;
    }

    const values = Array.isArray(value) ? value : [value];
    const answerSection = quizConfig.sections.find(
      (s) => (s.searchFilterKey || s.key) === key,
    );

    values.forEach((v) => {
      const option = answerSection?.options?.find(
        (opt) =>
          opt.value === v || opt.searchFilterValue === v || opt.label === v,
      );

      if (option?.facetMappings) {
        // Use custom facet mappings
        Object.entries(option.facetMappings).forEach(
          ([facetKey, facetValue]) => {
            const existing = appliedFacets.get(facetKey) || [];
            existing.push(facetValue);
            appliedFacets.set(facetKey, existing);
          },
        );
      } else {
        // Default behavior: use the key and value directly
        const existing = appliedFacets.get(key) || [];
        existing.push(v);
        appliedFacets.set(key, existing);
      }
    });
  });

  // Convert appliedFacets map to facetFilters array format
  appliedFacets.forEach((values, key) => {
    if (values.length > 0) {
      facetFilters.push(values.map((v) => `${key}:${v}`));
    }
  });

  return facetFilters;
};
